import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import './Events.css'
import { API_URL } from '../../api/base'
import { getLocalized } from '../../utils/localized'
import { formatDate as formatIntlDate, formatNumber } from '../../utils/date'

interface EventLocation {
  id: number
  name: { ru?: string; ky?: string; en?: string }
  latitude: number | null
  longitude: number | null
  regionId: number | null
}

interface Event {
  id: string
  slug: string
  name: { ru: string; ky: string }
  description: { ru: string; ky: string }
  locationName: string
  locations?: EventLocation[]
  eventDate: string
  targetTrees: number
  targetAmount: number
  status: 'planned' | 'active' | 'finished' | 'archived'
  coverImage: string | null
}

interface RegionInfo {
  id: number
  name: { ru?: string; ky?: string; en?: string }
}

export const EventsList: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [regionsById, setRegionsById] = useState<Map<number, RegionInfo>>(new Map())

  useEffect(() => {
    fetchEvents()
  }, [])

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ regions: RegionInfo[] }>(`${API_URL}/api/regions`)
        const map = new Map<number, RegionInfo>()
        ;(data.regions || []).forEach(r => map.set(r.id, r))
        setRegionsById(map)
      } catch (err) {
        console.error('regions load err', err)
      }
    })()
  }, [])

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/events`)
      setEvents(data.events)
    } catch (err) {
      setError(t('events_home.load_error'))
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) =>
    formatIntlDate(dateStr.replace('Z', ''), i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

  if (loading) {
    return <div className="events-loading">{t('loading')}</div>
  }

  if (error) {
    return <div className="events-error">{error}</div>
  }

  return (
    <div className="events-container">
      <h1 className="events-title">
        {t('events_home.title')}
      </h1>

      <div className="events-grid">
        {events.map(event => (
          <div key={event.id} className={`event-card event-status-${event.status}`}>
            {event.coverImage && (
              <div className="event-image">
                <img src={`${API_URL}${event.coverImage}`} alt={getLocalized(event.name, i18n.language)} />
              </div>
            )}

            <div className="event-content">
              <div className="event-status-badge">
                {t(`events_home.status_${event.status}`)}
              </div>

              <h2 className="event-name">{getLocalized(event.name, i18n.language)}</h2>

              <p className="event-description">
                {getLocalized(event.description, i18n.language)}
              </p>

              <div className="event-meta">
                {(() => {
                  const locs = event.locations || []
                  const first = locs[0]
                    ? getLocalized(locs[0].name, i18n.language)
                    : event.locationName
                  const extra = Math.max(0, locs.length - 1)
                  const region = locs[0]?.regionId != null
                    ? regionsById.get(locs[0].regionId)
                    : null
                  return (
                    <div className="event-meta-item" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span className="meta-icon">📍</span>
                      <span>{first || '—'}</span>
                      {region && (
                        <span style={{
                          font: "500 12px 'Golos Text',sans-serif",
                          color: '#5a6b4d',
                          background: '#eff5e6',
                          borderRadius: 999,
                          padding: '2px 9px',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.4
                        }}>
                          {getLocalized(region.name, i18n.language)}
                        </span>
                      )}
                      {extra > 0 && (
                        <span style={{ color: '#5a6b4d', fontSize: 13 }}>
                          · {t('festivals.and_more_places', { count: extra })}
                        </span>
                      )}
                    </div>
                  )
                })()}
                <div className="event-meta-item">
                  <span className="meta-icon">📅</span>
                  {formatDate(event.eventDate)}
                </div>
                {event.targetTrees && (
                  <div className="event-meta-item">
                    <span className="meta-icon">🌳</span>
                    {t('events_home.target', {
                      count: event.targetTrees,
                      amount: formatNumber(event.targetTrees, i18n.language)
                    })}
                  </div>
                )}
              </div>

              <a href={`/events/${event.slug}`} className="event-link">
                {t('events_home.more')} →
              </a>
            </div>
          </div>
        ))}
      </div>

      {events.length === 0 && (
        <div className="events-empty">
          {t('events_home.empty')}
        </div>
      )}
    </div>
  )
}
